import { useState } from 'react';
import { Button } from '@/shared/components/Button';
import { AlertTriangle } from 'lucide-react';
import { Icon } from '@/shared/components/Icon';
import { cn } from '@/shared/helpers/cn';
import styles from './ErrorState.module.scss';

export interface ErrorStateProps {
  /** O que falhou, na voz do produto. Sem "Ops!", sem culpa no usuario. */
  title?: string;
  message: string;
  /** Identificador da requisicao, para o suporte localizar a falha no log. */
  correlationId?: string | null;
  onRetry?: () => void;
  className?: string;
}

/**
 * Estado de erro de carregamento.
 *
 * Diz o que falhou e oferece "Tentar novamente". O `correlationId` fica copiavel
 * — e o que liga o relato do usuario a linha exata no log do servidor. Nenhum
 * codigo cru, stack ou SQL aparece aqui.
 */
export function ErrorState({
  title = 'Não foi possível carregar',
  message,
  correlationId,
  onRetry,
  className,
}: ErrorStateProps) {
  const [copied, setCopied] = useState(false);

  const copy = async (): Promise<void> => {
    if (!correlationId) return;
    try {
      await navigator.clipboard.writeText(correlationId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Sem permissao de area de transferencia o codigo continua visivel na tela.
    }
  };

  return (
    <div className={cn(styles.root, className)} role="alert">
      <span className={styles.icon}>
        <Icon as={AlertTriangle} size={20} />
      </span>
      <p className={styles.title}>{title}</p>
      <p className={styles.message}>{message}</p>

      {onRetry ? (
        <Button variant="secondary" onClick={onRetry}>
          Tentar novamente
        </Button>
      ) : null}

      {correlationId ? (
        <button type="button" className={styles.correlation} onClick={copy}>
          <span className={styles.correlationLabel}>Código da ocorrência</span>
          <code>{correlationId}</code>
          <span className={styles.copyHint}>{copied ? 'copiado' : 'copiar'}</span>
        </button>
      ) : null}
    </div>
  );
}
